/**
 * The EXTERNAL-SUBSYSTEM surfaces: the registry's External systems column, the
 * identity card's connection row, and the generic chart section every module
 * shares.
 *
 * The GENERIC words are the safe rendering for any module code this build
 * does not host — they never echo vendor claims, only what the server's
 * projection actually said. The `smartomica.*` keys are that module's OWN
 * registration words; the brand name itself is NEVER translated in any
 * locale, because a brand does not decline.
 */
export const externalModules = {
  'externalModule.genericConnection': 'External system',
  'externalModule.connectionsUnavailable': 'External systems unavailable',
  'externalModule.connectionsUnavailableDetail':
    'External system connection information could not be loaded. The patient records themselves are unaffected.',
  'externalModule.stateUnavailable': 'Unavailable',
  'externalModule.stateDisabled': 'Turned off',
  'externalModule.stateReconciliation': 'Needs reconciliation',
  'externalModule.badgeUnavailable': '{name}: connection unavailable',
  'externalModule.badgeDisabled': '{name}: module turned off',
  'externalModule.badgeReconciliation': '{name}: connection needs reconciliation',
  'externalModule.rowConnected': 'Connected',
  'externalModule.rowOpenSection': 'Open this patient’s {name} section',
  'externalModule.sectionTitle': 'External module',
  'externalModule.sectionLoadFailed': 'The connection state could not be loaded.',
  'externalModule.sectionUnknownTitle': 'This module is not available in this workspace',
  'externalModule.sectionUnknownDetail':
    'The link points to an external module this system does not host. The chart itself is unaffected.',
  'externalModule.sectionNotConnectedTitle': 'This patient is not connected',
  'externalModule.sectionNotConnectedDetail': 'No active connection to this module exists for this patient.',
  'externalModule.sectionUnavailableTitle': 'The module is currently unavailable',
  'externalModule.sectionUnavailableDetail':
    'The connection exists, but the module is not answering right now. Try again later.',
  'externalModule.sectionDisabledTitle': 'The module is turned off',
  'externalModule.sectionDisabledDetail':
    'The connection is kept, but this module is turned off for the clinic. Its section will return when the module is turned back on.',
  'externalModule.sectionReconciliationTitle': 'The connection needs reconciliation',
  'externalModule.sectionReconciliationDetail':
    'The link between this chart and the module must be reconciled before its section can open.',
  'externalModule.sectionBackToChart': 'Back to patient chart',
  'externalModule.workspaceLoading': 'Checking module availability…',
  'externalModule.workspaceUnavailableTitle': 'The module state could not be loaded',
  'externalModule.workspaceUnavailableDetail':
    'Whether this module is available for the clinic could not be determined right now. Try again later.',
  'externalModule.boardCountPending': 'Count not loaded yet',
  'externalModule.boardSectionPending': '{title}, count not loaded yet',
  'externalModule.alertsUnavailable': 'Alerts unavailable',
  'externalModule.alertsUnavailableDetail':
    'Open-alert counts could not be loaded. The patient records themselves are unaffected.',
  'externalModule.alertsOpenOne': '1 open',
  'externalModule.alertsOpenMany': '{count} open',
  'externalModule.alertsCritical': '{count} Urgent',
  'externalModule.alertsReview': '{count} Review',
  'externalModule.alertsCellNamed': 'Open the patient’s {name} workspace',
  'externalModule.alertsCellNamedEvent': 'Open this {name} alert',
  'externalModule.alertsFilterOpen': 'With open alerts',
  // The identity card's urgent row: the accessible name carries the count
  // and the module; the visible words say what it is; the title says where
  // the press goes — one event opens it, several open the Patient's list.
  // The wire's priority is `critical`; the word a clinician reads is "Urgent".
  'externalModule.criticalMarkOne': '1 urgent event open in {name}',
  'externalModule.criticalMarkMany': '{count} urgent events open in {name}',
  'externalModule.criticalRowOne': 'Urgent event',
  'externalModule.criticalRowMany': 'Urgent events',
  'externalModule.criticalOpenEvent': 'Open the event in {name}',
  'externalModule.criticalOpenList': 'Open this patient’s events in {name}',
  // The sign-in reminder's two controls; the module owns the title and body.
  'externalModule.reminderAcknowledge': 'OK',
  'externalModule.reminderGoToEvents': 'Go to events',
  'externalModule.smartomica.connected': 'Connected to Smartomica',
  'externalModule.smartomica.sectionLabel': 'Smartomica',
  'externalModule.smartomica.sectionDescription': 'External subsystem connected to this chart',
  'externalModule.smartomica.navLabel': 'Smartomica',
  'externalModule.smartomica.navDescription': 'External events, reviews and recommendations',
  'externalModule.smartomica.workspaceDisabledDetail':
    'Stored Smartomica events stay readable, but nothing new is fetched and no commands, recommendations or messages can run while the module is turned off.',
  // The wire vocabularies, one entry per closed value (words.ts owns the maps).
  'externalModule.smartomica.statusNew': 'New',
  'externalModule.smartomica.statusOpened': 'Opened',
  'externalModule.smartomica.statusUnderReview': 'Under review',
  'externalModule.smartomica.statusApproved': 'Approved',
  'externalModule.smartomica.statusClosed': 'Closed',
  'externalModule.smartomica.statusEnteredInError': 'Entered in error',
  'externalModule.smartomica.priorityCritical': 'Urgent',
  'externalModule.smartomica.priorityReview': 'Review',
  // The delivery ladder: each rung claims exactly itself. Provider acceptance
  // is not delivery, delivery is not reading.
  'externalModule.smartomica.deliveryPending': 'Sending',
  'externalModule.smartomica.deliverySimulated': 'Simulated send',
  'externalModule.smartomica.deliveryAccepted': 'Sent',
  'externalModule.smartomica.deliveryDelivered': 'Delivered',
  'externalModule.smartomica.deliveryRead': 'Read',
  'externalModule.smartomica.deliveryFailed': 'Send failed',
  'externalModule.smartomica.simulationEvidence': 'Simulation — no network provider was contacted.',
  'externalModule.smartomica.responseAccepted': 'Patient accepted the recommendation',
  'externalModule.smartomica.responseDeclined': 'Patient declined the recommendation',
  'externalModule.smartomica.responseClarification': 'Patient asked for clarification',
  'externalModule.smartomica.responseNone': 'No patient response recorded',
  'externalModule.smartomica.itemObservation': 'Note',
  'externalModule.smartomica.itemRecommendation': 'Action',
  'externalModule.smartomica.itemWarning': 'Alert',
  'externalModule.smartomica.itemFollowUp': 'Follow-up',
  // The flow queue.
  'externalModule.smartomica.flowTitle': 'Smartomica Flow',
  'externalModule.smartomica.flowLabel': 'Smartomica events',
  'externalModule.smartomica.flowLoading': 'Loading the event queue…',
  'externalModule.smartomica.flowLoadFailed': 'The event queue could not be loaded.',
  'externalModule.smartomica.flowEmptyTitle': 'No events',
  'externalModule.smartomica.flowEmptyDetail': 'Smartomica has not sent any events for this clinic yet.',
  'externalModule.smartomica.flowNoMatchesTitle': 'No matching events',
  'externalModule.smartomica.flowNoMatchesDetail':
    'No events match the applied filters. Clear them to see the whole queue.',
  'externalModule.smartomica.columnPatient': 'Patient',
  'externalModule.smartomica.columnEvent': 'Event',
  'externalModule.smartomica.columnValue': 'Value',
  'externalModule.smartomica.columnReceived': 'Received',
  'externalModule.smartomica.columnPriority': 'Priority',
  'externalModule.smartomica.columnStatus': 'Status',
  'externalModule.smartomica.columnAssigned': 'Assigned',
  'externalModule.smartomica.columnReviewed': 'Reviewed',
  'externalModule.smartomica.columnOutcome': 'Sent to patient',
  'externalModule.smartomica.patientUnresolved': 'Patient not resolved',
  'externalModule.smartomica.unassigned': 'Unassigned',
  'externalModule.smartomica.assignedUnresolved': 'Name not resolved',
  'externalModule.smartomica.assignedApplied': 'Applied assignee (not on this page)',
  'externalModule.smartomica.reviewedNone': 'Not reviewed',
  'externalModule.smartomica.reviewClinicianUnresolved': 'Clinician not resolved',
  'externalModule.smartomica.outcomeNoMessage': 'Not sent',
  'externalModule.smartomica.outcomeSimulated': 'Simulated',
  'externalModule.smartomica.measuredAt': 'Measured {time}',
  'externalModule.smartomica.receivedFrom': 'Received from',
  'externalModule.smartomica.receivedTo': 'Received to',
  'externalModule.smartomica.priorityLocalTitle': 'Priority assigned by this system’s rule, not stated by the source',
  'externalModule.smartomica.prioritySourceTitle': 'Priority stated by the source',
  'externalModule.smartomica.priorityOpensReview': '{priority} — open the review of {name}’s event',
  'externalModule.smartomica.priorityOpensEventReview': '{priority} — open the review of {name}',
  // The event review page: the head, the progress strip, the Event card.
  // Nothing on this surface names what produces the analytics, and a rule
  // test keeps it that way.
  'externalModule.smartomica.eventTitle': 'Event review',
  'externalModule.smartomica.dobLine': 'DOB {date}',
  'externalModule.smartomica.eventLoading': 'Loading the event…',
  'externalModule.smartomica.eventLoadFailed': 'The event could not be loaded.',
  'externalModule.smartomica.backToFlow': 'Back to Smartomica Flow',
  'externalModule.smartomica.backToPatientSection': 'Back to the patient chart',
  'externalModule.smartomica.blockEvent': 'Event',
  'externalModule.smartomica.workflowLabel': 'Event progress',
  'externalModule.smartomica.workflowReceived': 'Received',
  'externalModule.smartomica.workflowOpened': 'Opened',
  'externalModule.smartomica.workflowAnalytics': 'Analytics ready',
  'externalModule.smartomica.workflowApproved': 'Approved',
  'externalModule.smartomica.workflowSent': 'Sent to patient',
  'externalModule.smartomica.workflowComplete': 'Completed',
  'externalModule.smartomica.workflowCurrent': 'Current step',
  'externalModule.smartomica.workflowPending': 'Not completed',
  'externalModule.smartomica.workflowFailed': 'Failed',
  // The Event card's rows: source, parameter, value with its range, time, note.
  'externalModule.smartomica.sourceLabel': 'Source',
  'externalModule.smartomica.parameterLabel': 'Parameter',
  'externalModule.smartomica.valueLabel': 'Value',
  'externalModule.smartomica.rangeLabel': 'Normal range',
  'externalModule.smartomica.aboveRange': 'above the normal range',
  'externalModule.smartomica.belowRange': 'below the normal range',
  'externalModule.smartomica.measuredLabel': 'Measured',
  'externalModule.smartomica.noteSleepQuality': 'Sleep quality',
  'externalModule.smartomica.noteSleepComment': 'Sleep comment',
  'externalModule.smartomica.noteAnxiety': 'Anxiety',
  'externalModule.smartomica.noteSymptoms': 'Symptoms',
  'externalModule.smartomica.noteSymptomComment': 'Symptom comment',
  'externalModule.smartomica.notePatientComment': 'Patient comment',
  'externalModule.smartomica.notePolydipsia': 'Excessive thirst',
  'externalModule.smartomica.notePolyuria': 'Frequent urination',
  'externalModule.smartomica.noteMarkedWeakness': 'Marked weakness',
  'externalModule.smartomica.noteNausea': 'Nausea',
  'externalModule.smartomica.noteVomiting': 'Vomiting',
  'externalModule.smartomica.noteAbdominalPain': 'Abdominal pain',
  'externalModule.smartomica.noteConfusion': 'Confusion',
  'externalModule.smartomica.noteLossOfConsciousness': 'Loss of consciousness',
  'externalModule.smartomica.noteChestPain': 'Chest pain',
  'externalModule.smartomica.noteDyspnea': 'Shortness of breath',
  'externalModule.smartomica.noteFatigue': 'Fatigue',
  'externalModule.smartomica.noteColdIntolerance': 'Cold intolerance',
  'externalModule.smartomica.noteConstipation': 'Constipation',
  'externalModule.smartomica.noteDrySkin': 'Dry skin',
  'externalModule.smartomica.noteHairLoss': 'Hair loss',
  'externalModule.smartomica.noteWeightGain': 'Weight gain',
  'externalModule.smartomica.notePalpitations': 'Palpitations',
  'externalModule.smartomica.noteTremor': 'Tremor',
  'externalModule.smartomica.noteMenstrualCycleChange': 'Menstrual cycle changes',
  'externalModule.smartomica.noteSleepPoor': 'Poor',
  'externalModule.smartomica.noteSleepFair': 'Fair',
  'externalModule.smartomica.noteSleepGood': 'Good',
  'externalModule.smartomica.noteAnxietyNone': 'None',
  'externalModule.smartomica.noteAnxietyMild': 'Mild',
  'externalModule.smartomica.noteAnxietyModerate': 'Moderate',
  'externalModule.smartomica.noteAnxietySevere': 'Severe',
  'externalModule.smartomica.noteYes': 'Yes',
  'externalModule.smartomica.noteNo': 'No',
  'externalModule.smartomica.noteNotProvided': 'Not provided',
  'externalModule.smartomica.patientCommentLabel': 'Patient note',
  'externalModule.smartomica.manualFallbackEvent': 'Analytics unavailable review',
  'externalModule.smartomica.manualFallbackSeedNote': 'Synthetic Smartomica evidence demonstrating manual review when analytics are unavailable.',
  'externalModule.smartomica.patientCommentNone': 'No note from the patient',
  'externalModule.smartomica.alsoReported': 'Also reported',
  // Questions about the event document and the current doctor’s conversation.
  'externalModule.smartomica.askTitle': 'Ask any question about the patient’s medical history',
  'externalModule.smartomica.askPlaceholder': 'When was the last HbA1c? · Which medications is the patient taking?',
  'externalModule.smartomica.askButton': 'Ask',
  'externalModule.smartomica.askClinicianMark': 'DR',
  'externalModule.smartomica.askPending': "Answering…",
  'externalModule.smartomica.askLoading': "Loading your conversation…",
  'externalModule.smartomica.askEmpty': "No questions yet. Your conversation is private to you for this event.",
  'externalModule.smartomica.askFailed': "Could not get an answer. Your question is preserved; try again.",
  'externalModule.smartomica.askMissingSummary': "This event has no Clinical Summary PDF.",
  'externalModule.smartomica.askAlreadyRunning': "A question is already being answered. Try again shortly.",
  'externalModule.smartomica.askRequestReused': "This request was already used for another question. Enter a new question.",
  'externalModule.smartomica.askHistoryLimit': "This conversation has reached the supported context size.",
  'externalModule.smartomica.askReadOnly': "Questions are unavailable with your current access or module state.",
  'externalModule.smartomica.askBeforeReview': "Questions are available once you start the review.",
  'externalModule.smartomica.askAfterReview': "Questions are available only while the review is open.",
  'externalModule.smartomica.askHistoryFailed': "Could not load your conversation.",
  'externalModule.smartomica.askUnsaved': "You have an unsent question about this Clinical Summary.",
  'externalModule.smartomica.askNote': 'Answers are based only on the summary below.',
  // The Clinical Summary card: the document the source sent, read as text.
  'externalModule.smartomica.summaryTitle': 'Clinical Summary',
  'externalModule.smartomica.summaryReceivedFrom': 'received from JWL',
  'externalModule.smartomica.summaryDownload': 'Download',
  'externalModule.smartomica.summaryLoading': 'Reading the summary…',
  'externalModule.smartomica.summaryUnreadable': 'The summary text could not be read. Download the document to read it.',
  'externalModule.smartomica.summaryShowFull': 'Show full summary',
  'externalModule.smartomica.summaryShowLess': 'Show less',
  // The analytics panel and the one press that starts a review.
  'externalModule.smartomica.analyticsTitle': 'Smartomica Analytics',
  'externalModule.smartomica.startReview': 'Start review',
  'externalModule.smartomica.analyticsLoading': 'Loading recommendations…',
  'externalModule.smartomica.analyticsFailed': 'The recommendations could not be loaded.',
  'externalModule.smartomica.analyticsWriteInstead': 'Write the recommendation below.',
  'externalModule.smartomica.analyticsRetry': 'Try again',
  'externalModule.smartomica.analyticsPartial':
    'Some recommendations could not be loaded; the list may be incomplete. Review it before approval.',
  'externalModule.smartomica.analyticsNone': 'No recommendations yet.',
  'externalModule.smartomica.analyticsLoad': 'Load recommendations',
  'externalModule.smartomica.reviewNotOffered': 'Reviewing is not offered to this account.',
  'externalModule.smartomica.approvalNotOffered': 'Approval and sending are not offered to this account.',
  'externalModule.smartomica.editRecommendation': 'Edit recommendation {number}',
  'externalModule.smartomica.recommendationRequired': 'Each recommendation must contain text.',
  // The clinician's own words. "Dictate" is drawn and not wired yet.
  'externalModule.smartomica.writingTitle': 'Doctor’s writing',
  'externalModule.smartomica.writingPlaceholder': 'Write or dictate your text',
  'externalModule.smartomica.dictationTitle': 'Dictation',
  'externalModule.smartomica.dictationCancel': 'Cancel dictation',
  'externalModule.smartomica.dictationIntro': 'Record your text. When you stop, the recognized words are added to the field in the language you spoke.',
  'externalModule.smartomica.dictationStop': 'Stop and insert',
  'externalModule.smartomica.dictationProcessing': 'Recognizing speech…',
  'externalModule.smartomica.dictationTooLong': 'The recognized text does not fit this field. Record a shorter passage.',
  'externalModule.smartomica.dictationTooLarge': 'The recording is larger than 25 MB. Record a shorter passage.',
  'externalModule.smartomica.dictationForbidden': 'You no longer have access to dictate for this event.',
  'externalModule.smartomica.dictationUnsaved': 'Smartomica recording being transcribed',
  'externalModule.smartomica.askMic': 'Dictate the question',
  'externalModule.smartomica.dictate': 'Dictate',
  'externalModule.smartomica.unsavedDraft': 'Smartomica recommendation draft',
  'externalModule.smartomica.draftSaving': 'Saving the draft…',
  'externalModule.smartomica.draftSaved': 'Draft saved {time}',
  'externalModule.smartomica.draftUnsaved': 'Unsaved changes',
  'externalModule.smartomica.previewHeading': 'Message the patient will receive',
  'externalModule.smartomica.previewEmpty': 'Select at least one item or write a comment.',
  'externalModule.smartomica.attestation': 'I confirm that I have reviewed this message and approve it as my recommendation.',
  'externalModule.smartomica.sending': 'Sending…',
  'externalModule.smartomica.approvedAt': 'Approved {time}',
  'externalModule.smartomica.credentialLine': 'Licence {licence}, {issuer}, {jurisdiction}',
  'externalModule.smartomica.messageHeading': 'Message to the patient',
  'externalModule.smartomica.deliveryLabel': 'Delivery',
  'externalModule.smartomica.responseLabel': 'Patient response',
  'externalModule.smartomica.sendToPatient': 'Send to patient',
  'externalModule.smartomica.retrySend': 'Retry send',
  'externalModule.smartomica.commandFailed': 'The action could not be completed.',
  'externalModule.smartomica.commandDisabled': 'The module is turned off; commands are not available.',
  'externalModule.smartomica.conflictNotice':
    'The event changed while you were working. The latest facts were reloaded; your text was kept — review it and try again.',
  'externalModule.smartomica.notReviewable': 'This event is not in a reviewable state.',
  'externalModule.smartomica.credentialRefused':
    'Your clinician credential could not be verified, so approval was refused.',
  // The dashboard board section.
  'externalModule.smartomica.dashboardTitle': 'Smartomica',
  'externalModule.smartomica.dashboardUnit': 'open events',
  'externalModule.smartomica.dashboardLink': 'Open Flow',
  'externalModule.smartomica.dashboardLoading': 'Loading the summary…',
  'externalModule.smartomica.dashboardLoadFailed': 'The summary could not be loaded.',
  // The board tile's counter pills: what arrived over the served clinic days,
  // each name a pill's hover hint.
  'externalModule.smartomica.sparkCritical': 'Urgent',
  'externalModule.smartomica.sparkReview': 'For review',
  'externalModule.smartomica.sparkNewPatients': 'New patients',
  'externalModule.smartomica.sparkPeriod': '{days}-day arrivals',
  // The sign-in reminder.
  'externalModule.smartomica.reminderTitle': 'Urgent Smartomica events are open',
  'externalModule.smartomica.reminderBodyOne':
    '1 urgent event is waiting for a decision. Open it to review and answer the patient.',
  'externalModule.smartomica.reminderBodyMany':
    '{count} urgent events are waiting for a decision. Open them to review and answer the patients.',
  'externalModule.smartomica.recentTitle': 'Recent events',
  'externalModule.smartomica.recentEmpty': 'No recent events.',
  // The chart section.
  'externalModule.smartomica.sectionEventsLoading': 'Loading this patient’s events…',
  'externalModule.smartomica.patientEventsFailed': 'This patient’s Smartomica events could not be loaded.',
  'externalModule.smartomica.patientEventsTitle': 'Events',
  'externalModule.smartomica.patientEventsLabel': 'Smartomica events for this patient',
  'externalModule.smartomica.patientEventsEmptyTitle': 'No Smartomica events',
  'externalModule.smartomica.patientEventsEmptyDetail': 'Smartomica has not sent any events for this patient.',
  'externalModule.smartomica.patientEventsNoMatchesTitle': 'No matching events',
  'externalModule.smartomica.patientEventsNoMatchesDetail':
    'No events match the table filters. Clear them to see every event.',
  'externalModule.smartomica.patientEventsClosedHidden': 'Closed events hidden',
  'externalModule.smartomica.patientEventsClosedShow': 'Show closed events',
  'externalModule.smartomica.reviewHistoryTitle': 'Review history',
  'externalModule.smartomica.reviewHistoryLoading': 'Loading review history…',
  'externalModule.smartomica.reviewHistoryFailed': 'The review history could not be loaded.',
  'externalModule.smartomica.reviewHistoryEmpty': 'No committed reviews yet.',
  'externalModule.smartomica.reviewHistoryNoneOnPage':
    'No Smartomica reviews on the loaded part of the timeline. Load more to keep looking.',
  'externalModule.smartomica.reviewHistoryMore': 'Load more history',
  // The Patients-registry alerts column.
  'externalModule.smartomica.alertsColumnLabel': 'Alerts',
  'externalModule.smartomica.alertsColumnDescription':
    'Open Smartomica events per patient: total, urgent and for-review counts.',
  // Control Center administration.
  'externalModule.smartomica.settings.integration': 'Integration connection',
  'externalModule.smartomica.settings.loading': 'Loading integration settings…',
  'externalModule.smartomica.settings.loadFailed': 'The Smartomica settings could not be loaded.',
  'externalModule.smartomica.settings.forbidden': 'This account may not manage Smartomica settings.',
  'externalModule.smartomica.settings.endpoint': 'Integration endpoint',
  'externalModule.smartomica.settings.clientCode': 'Client code',
  'externalModule.smartomica.settings.token': 'Token',
  'externalModule.smartomica.settings.tokenStored': 'Stored securely; the value is never returned.',
  'externalModule.smartomica.settings.tokenReplaceHint': 'Leave unchanged to keep the stored token, or enter a new token to replace it.',
  'externalModule.smartomica.settings.tokenRequiredHint': 'Enter a token containing 32 to 256 characters.',
  'externalModule.smartomica.settings.change': 'Change integration settings',
  'externalModule.smartomica.settings.set': 'Set integration settings',
  'externalModule.smartomica.settings.save': 'Save settings',
  'externalModule.smartomica.settings.saving': 'Saving settings…',
  'externalModule.smartomica.settings.saved': 'Smartomica settings saved.',
  'externalModule.smartomica.settings.saveFailed': 'The Smartomica settings could not be saved.',
  'externalModule.smartomica.settings.conflict': 'The settings changed elsewhere. Reload them before saving again.',
  'externalModule.smartomica.settings.unsaved': 'Smartomica settings contain unsaved changes.',
  'externalModule.smartomica.debug.title': 'Smartomica debug',
  'externalModule.smartomica.debug.subtitle':
    "Prepared events submitted through the ordinary Smartomica ingress; external effects use this contour's configured providers.",
  'externalModule.smartomica.debug.target': 'Target Patient',
  'externalModule.smartomica.debug.patient': 'Patient for linked events',
  'externalModule.smartomica.debug.patientPlaceholder': 'Find a Patient',
  'externalModule.smartomica.debug.patientNone': 'No Patient selected',
  'externalModule.smartomica.debug.patientDirectoryUnavailable': 'This account cannot read the Patient identity directory, so linked scenarios are unavailable.',
  'externalModule.smartomica.debug.moduleState': 'Module readiness',
  'externalModule.smartomica.debug.moduleReady': 'Enabled and visible; debug ingress is available.',
  'externalModule.smartomica.debug.moduleInactive': 'Turn on and show the module before submitting debug events.',
  'externalModule.smartomica.debug.scenarios': 'Prepared events',
  'externalModule.smartomica.debug.incoming_patient.title': 'Incoming Patient',
  'externalModule.smartomica.debug.incoming_patient.detail': 'Creates a synthetic Patient, assigns the configured doctor and notifies them. If a Patient is selected, the new person borrows that chart’s name, birth date and sex; the new Smartomica identity still creates a separate chart.',
  'externalModule.smartomica.debug.existing_patient.title': 'Existing Patient',
  'externalModule.smartomica.debug.existing_patient.detail': 'Sends an incoming event with one mapped heart-rate observation for the selected linked Patient.',
  'externalModule.smartomica.debug.vital_signs.title': 'Vital signs',
  'externalModule.smartomica.debug.vital_signs.detail': 'Sends mapped heart rate and blood pressure for the selected linked Patient.',
  'externalModule.smartomica.debug.review.title': 'Review event',
  'externalModule.smartomica.debug.review.detail': 'Sends TSH 6.2 with Free T4 14.5, vitamin D 18 and reported fatigue, anxiety and poor sleep for review.',
  'externalModule.smartomica.debug.critical.title': 'Urgent event',
  'externalModule.smartomica.debug.critical.detail': 'Sends fasting glucose 288 mg/dL with positive ketones, vomiting, abdominal pain and thirst, and triggers an immediate alert.',
  'externalModule.smartomica.debug.send': 'Send event',
  'externalModule.smartomica.debug.sending': 'Sending…',
  'externalModule.smartomica.debug.result': 'Latest result',
  'externalModule.smartomica.debug.resultState': 'Ingress result',
  'externalModule.smartomica.debug.resultCandidate': 'Accepted for Patient reconciliation.',
  'externalModule.smartomica.debug.resultAccepted': 'Accepted and attached to the selected Patient.',
  'externalModule.smartomica.debug.openEvent': 'Open the created event',
  'externalModule.smartomica.debug.patientNotLinked': 'The selected Patient has no active Smartomica identity. Link the Patient in reconciliation first.',
  'externalModule.smartomica.debug.forbidden': 'This account may no longer run Smartomica debug events.',
  'externalModule.smartomica.debug.unavailable': 'The module is off or the Smartomica debug contour is unavailable.',
  'externalModule.smartomica.debug.failed': 'The prepared Smartomica event could not be submitted.',
  // The incoming Patients — the reconciliation queue and its decision panel.
  'externalModule.smartomica.incomingTitle': 'Incoming patients',
  'externalModule.smartomica.incomingDetail':
    'People Smartomica sent whose identity is not linked to any Patient yet. Each waits for a decision: link an existing Patient or register a new one.',
  'externalModule.smartomica.incomingCount': 'Waiting: {count}',
  'externalModule.smartomica.incomingBackToFlow': 'Back to Smartomica Flow',
  'externalModule.smartomica.incomingLink.counted': 'Incoming patients: {count}',
  'externalModule.smartomica.incomingLink.uncounted': 'Incoming patients',
  'externalModule.smartomica.incomingDisabledDetail':
    'Incoming identities stay readable, but linking and registering are not available while the module is turned off.',
  'externalModule.smartomica.incomingLoading': 'Loading incoming patients…',
  'externalModule.smartomica.incomingLoadFailed': 'The incoming patients could not be loaded.',
  'externalModule.smartomica.incomingEmptyTitle': 'No incoming patients',
  'externalModule.smartomica.incomingEmptyDetail': 'Every identity Smartomica sent is linked to a Patient.',
  'externalModule.smartomica.incomingListLabel': 'Incoming patients awaiting a decision',
  'externalModule.smartomica.incomingUnnamed': 'Unnamed person',
  'externalModule.smartomica.incomingFactUnknown': 'Not stated',
  'externalModule.smartomica.incomingSexUnknown': 'Not stated',
  'externalModule.smartomica.incomingReceived': 'First received',
  'externalModule.smartomica.incomingHeld': 'Held events',
  'externalModule.smartomica.incomingHeldCount': '{count}',
  'externalModule.smartomica.incomingMatchesTitle': 'Possible existing Patients: {count}',
  'externalModule.smartomica.incomingMatchesFor': 'Possible existing Patients for {name}',
  'externalModule.smartomica.incomingNoMatchTitle': 'No matching Patient in the register',
  'externalModule.smartomica.incomingNoMatchDetail':
    'No active Patient in this clinic shares this person’s name, or their birth date with one of the names.',
  'externalModule.smartomica.incomingNameAgrees': 'Name agrees',
  'externalModule.smartomica.incomingBirthDateAgrees': 'Birth date agrees',
  'externalModule.smartomica.incomingDecide': 'Decide',
  'externalModule.smartomica.incomingDecisionEyebrow': 'Incoming patient',
  'externalModule.smartomica.incomingChooseLegend': 'Link to which Patient?',
  'externalModule.smartomica.incomingAnotherPatient': 'Another Patient from the register',
  'externalModule.smartomica.incomingAnotherPatientField': 'Patient',
  'externalModule.smartomica.incomingChosenPatient': 'the chosen Patient',
  'externalModule.smartomica.incomingChooseFirst': 'Choose the Patient to link first.',
  'externalModule.smartomica.incomingLink': 'Link Patient',
  'externalModule.smartomica.incomingLinking': 'Linking…',
  'externalModule.smartomica.incomingRegisterNew': 'Register as new Patient',
  'externalModule.smartomica.incomingRetryLink': 'Link the registered Patient',
  'externalModule.smartomica.incomingRegisteredNotLinked':
    '{name} was registered, but the Smartomica identity could not be linked yet. The chart is kept; link it again.',
  'externalModule.smartomica.registerReason': 'Registered as a new Patient from the incoming Smartomica identity.',
  'externalModule.smartomica.incomingConfirmTitle': 'Link this Patient to the Smartomica identity?',
  'externalModule.smartomica.incomingConfirmDetail':
    'The incoming person “{incoming}” will be linked to {patient}. Held events are released into the Flow and appear on the chart.',
  'externalModule.smartomica.incomingConfirmCancel': 'Keep deciding',
  'externalModule.smartomica.incomingReasonLegend': 'Why is this the same person?',
  'externalModule.smartomica.linkReasonSamePerson': 'Same person by name and date of birth',
  'externalModule.smartomica.linkReasonConfirmed': 'Confirmed with the patient',
  'externalModule.smartomica.linkReasonSourceIdentifier': 'Matched by the source’s own identifier',
  'externalModule.smartomica.incomingReasonOther': 'Other reason',
  'externalModule.smartomica.incomingReasonDetail': 'Reason detail',
  'externalModule.smartomica.incomingReasonHint': 'The reason is recorded with the link and cannot be edited later.',
  'externalModule.smartomica.incomingLinkedNotice': 'Linked to {name}. The entry has left the incoming list.',
  'externalModule.smartomica.incomingOpenChart': 'Open the chart',
  'externalModule.smartomica.recipientMismatch': 'This patient is assigned to another doctor in Smartomica. Select a patient assigned to you.',
  'externalModule.smartomica.alreadyConnected': 'This Patient already has an active Smartomica identity. The incoming Patient has not been linked.',
  'externalModule.smartomica.connectionChanged': 'The Smartomica connection changed. Cancel and try linking again to confirm the current connection.',
  'externalModule.smartomica.replaceTitle': 'Replace the Smartomica connection?',
  'externalModule.smartomica.replaceDetail': '{patient} already has an active Smartomica identity. Replace it with the identity of this incoming Patient? Future data will use the new connection. Previously received events and clinical history will be preserved. The old identity will no longer accept new data.',
  'externalModule.smartomica.replaceConfirm': 'Replace and link',
  'externalModule.smartomica.discardIncoming': 'Discard incoming Patient',
  'externalModule.smartomica.discardDetail': 'Discard the incoming entry for {name} without linking it to a chart? Only the input waiting in this entry will be discarded. Existing Patient events and clinical history will remain unchanged. The source and your reason will be retained.',
  'externalModule.smartomica.discardFailed': 'The incoming Patient could not be discarded.',
  'externalModule.smartomica.discardReasonLegend': 'Why are you discarding this incoming Patient?',
  'externalModule.smartomica.discardReasonTest': 'Test entry',
  'externalModule.smartomica.discardReasonWrongPatient': 'Received in error',
  'externalModule.smartomica.reconcileFailed': 'The Patient could not be linked.',
  'externalModule.smartomica.reconcileConflict': 'This entry changed while you were deciding. Reload the list and decide again.',
  'externalModule.smartomica.reconcileAlreadyLinked': 'This entry is already linked to a Patient.',
  'externalModule.smartomica.reconcileGone': 'This entry is no longer waiting.',
  'externalModule.smartomica.reconcilePatientNotFound': 'The chosen Patient was not found in this clinic.',
  "externalModule.ondoc.connected": "Connected to OnDoc",
  "externalModule.ondoc.sectionLabel": "OnDoc connection",
  "externalModule.ondoc.connectionDescription": "Patient attachment and connection codes",
  "externalModule.ondoc.navLabel": "OnDoc",
  "externalModule.ondoc.navDescription": "Booking requests, the exchange journal and patient attachment",
  "externalModule.ondoc.areaNavigation": "OnDoc sections",
  "externalModule.ondoc.bookingsNavDetail": "Requests from OnDoc awaiting a chart",
  "externalModule.ondoc.journalNavDetail": "What this clinic last exchanged with OnDoc",
  "externalModule.ondoc.attachmentView": "Patient attachment",
  "externalModule.ondoc.attachmentNavDetail": "Attachment state and connection codes for one chart",
  "externalModule.ondoc.choosePatient": "Choose a patient",
  "externalModule.ondoc.choosePatientDetail": "Pick a chart above to see what it holds in OnDoc.",
  "externalModule.ondoc.readFailed": "OnDoc connection information could not be loaded.",
  "externalModule.ondoc.codeFailed": "Could not obtain the code. The request may have succeeded; check OnDoc before requesting another code.",
  "externalModule.ondoc.disabled": "OnDoc is disabled. Stored connection information remains available.",
  "externalModule.ondoc.configurationRequired": "Ask an administrator to configure OnDoc for this organization.",
  "externalModule.ondoc.configurationChanged": "The connection settings changed. Reload the information before requesting a code.",
  "externalModule.ondoc.profileIncomplete": "Check the patient’s name, date of birth and contact information before requesting a connection code.",
  "externalModule.ondoc.branchUnavailable": "Branch unavailable",
  "externalModule.ondoc.network": "Entire organization network",
  "externalModule.ondoc.scope": "Attachment scope",
  "externalModule.ondoc.status": "Attachment",
  "externalModule.ondoc.attached": "Attached",
  "externalModule.ondoc.detached": "Detached",
  "externalModule.ondoc.connectedAt": "Attached on",
  "externalModule.ondoc.disconnectedAt": "Detached on",
  "externalModule.ondoc.loading": "Loading connection information…",
  "externalModule.ondoc.attachments": "OnDoc attachment by scope",
  "externalModule.ondoc.noAttachment": "No confirmed attachment",
  "externalModule.ondoc.noAttachmentDetail": "Issuing a connection code does not confirm patient attachment.",
  "externalModule.ondoc.noMatches": "No matching attachments",
  "externalModule.ondoc.noMatchesDetail": "Change the table filters.",
  "externalModule.ondoc.codeTitle": "Connection code",
  "externalModule.ondoc.branch": "Branch",
  "externalModule.ondoc.branchesFailed": "The branch list could not be loaded.",
  "externalModule.ondoc.requestingCode": "Requesting code…",
  "externalModule.ondoc.requestCode": "Request connection code",
  "externalModule.ondoc.codeLabel": "Connection code:",
  "externalModule.ondoc.codeHint": "Give this code to the patient. Attachment is confirmed by OnDoc separately; refresh to see its latest state.",
  "externalModule.ondoc.patient": "Patient",
  "externalModule.ondoc.eventUnavailable": "This OnDoc entry is unavailable.",
  "externalModule.ondoc.refresh": "Refresh attachment",
  "externalModule.ondoc.bookingsFailed": "The booking queue could not be loaded.",
  "externalModule.ondoc.resolveFailed": "The chart could not be confirmed. Keep the selected chart and refresh the queue before trying again.",
  "externalModule.ondoc.bookingChanged": "This request has changed. Refresh the queue before choosing a chart again.",
  "externalModule.ondoc.awaitingRegistrar": "Awaiting chart selection",
  "externalModule.ondoc.bookingFailed": "Processing failed",
  "externalModule.ondoc.bookingAmbiguous": "OnDoc result requires verification",
  "externalModule.ondoc.bookingUnknown": "Unknown processing state",
  "externalModule.ondoc.bookingStart": "Appointment time",
  "externalModule.ondoc.bookingActions": "Booking actions",
  "externalModule.ondoc.bookingsRefresh": "Refresh queue",
  "externalModule.ondoc.bookingOpen": "Review booking request",
  "externalModule.ondoc.bookingOpenNamed": "Review the booking request of {name}",
  "externalModule.ondoc.bookings": "Incoming booking requests",
  "externalModule.ondoc.noBookings": "No requests awaiting processing",
  "externalModule.ondoc.noBookingsDetail": "Unresolved OnDoc booking requests will appear here.",
  "externalModule.ondoc.bookingUnsaved": "Unconfirmed chart selection for an OnDoc booking",
  "externalModule.ondoc.resolveBooking": "Confirm chart and process booking",
  "externalModule.ondoc.bookingFactsHint": "These facts came from OnDoc. Selecting a chart does not change its demographics or connect the patient to OnDoc.",
  "externalModule.ondoc.birthDate": "Date of birth supplied by OnDoc",
  "externalModule.ondoc.phone": "Phone supplied by OnDoc",
  "externalModule.ondoc.email": "Email supplied by OnDoc",
  "externalModule.ondoc.comment": "Patient comment",
  "externalModule.ondoc.chartCreated": "Chart registered; confirm it for this booking:",
  "externalModule.ondoc.chooseChart": "Select the patient chart",
  "externalModule.ondoc.registerChart": "Register a new patient chart",
  "externalModule.ondoc.bookingDiscardDetail": "Only the unconfirmed selection will be cleared. A chart already registered will remain.",
  "externalModule.ondoc.keepChoice": "Keep selection",
  "externalModule.ondoc.discardChoice": "Clear selection",
  "externalModule.ondoc.settings.title": "Connection settings",
  "externalModule.ondoc.settings.failed": "Settings could not be saved. Check the mappings and required fields.",
  "externalModule.ondoc.settings.conflict": "The configuration or module state has changed. Read the current settings before continuing.",
  "externalModule.ondoc.settings.connectionLocked": "The environment, network and attachment scope of an existing connection require reconciliation before replacement.",
  "externalModule.ondoc.settings.bindingLocked": "An existing provider binding cannot be reassigned. Restore the saved mapping.",
  "externalModule.ondoc.settings.priceInvalid": "Select an active public price list for each branch with the configured currency and two decimal places.",
  "externalModule.ondoc.settings.serviceInvalid": "Check the selected services and their OnDoc specializations.",
  "externalModule.ondoc.settings.typeInvalid": "Select a published appointment type with the matching duration.",
  "externalModule.ondoc.settings.scheduleInvalid": "Check the branch, doctor, workplace and services. One schedule uses one published appointment type version.",
  "externalModule.ondoc.settings.forbidden": "You do not have permission to manage these settings.",
  "externalModule.ondoc.settings.unsaved": "Unsaved OnDoc connection settings",
  "externalModule.ondoc.settings.loadFailed": "The connection settings could not be loaded.",
  "externalModule.ondoc.settings.resumed": "Exchange is resumed for the current configuration.",
  "externalModule.ondoc.settings.paused": "Exchange is paused. Review the settings before resuming.",
  "externalModule.ondoc.settings.notConfigured": "OnDoc is not configured for this organization yet.",
  "externalModule.ondoc.settings.edit": "Review or edit settings",
  "externalModule.ondoc.settings.resume": "Resume exchange",
  "externalModule.ondoc.settings.savePauses": "Saving pauses exchange. Resume it separately after reviewing the saved settings.",
  "externalModule.ondoc.settings.environment": "OnDoc environment",
  "externalModule.ondoc.settings.stage": "Test environment",
  "externalModule.ondoc.settings.production": "Production environment",
  "externalModule.ondoc.settings.network": "OnDoc network identifier",
  "externalModule.ondoc.settings.attachmentMode": "Patient attachment scope",
  "externalModule.ondoc.settings.publicToken": "Network Public API key",
  "externalModule.ondoc.settings.tokenRetained": "A key is stored. Leave this field empty to keep it.",
  "externalModule.ondoc.settings.tokenRequired": "Enter the key supplied by OnDoc.",
  "externalModule.ondoc.settings.currency": "OnDoc price currency code",
  "externalModule.ondoc.settings.horizon": "Days of availability to publish, including today (1–365)",
  "externalModule.ondoc.settings.save": "Save settings",
  "externalModule.ondoc.settings.cancel": "Cancel editing",
  "externalModule.ondoc.settings.reload": "Replace the draft with current saved settings",
  "externalModule.ondoc.settings.reloadFailed": "Current settings could not be loaded. Your draft is retained.",
  "externalModule.ondoc.settings.discardDetail": "Discard the unsaved settings and keys entered in this form? Stored keys will remain.",
  "externalModule.ondoc.settings.sourceUnavailable": "Saved source is unavailable",
  "externalModule.ondoc.settings.removeMapping": "Remove this mapping",
  "externalModule.ondoc.settings.branches": "Branches and public price lists",
  "externalModule.ondoc.settings.services": "Services",
  "externalModule.ondoc.settings.doctors": "Doctors",
  "externalModule.ondoc.settings.schedules": "Booking schedules",
  "externalModule.ondoc.settings.mappingHint": "Select existing Terysling sources. Saving these mappings does not edit price lists, doctors or schedules. New publication identifiers are allocated by the integration.",
  "externalModule.ondoc.settings.sourcesFailed": "A mapping source could not be loaded. Saved selections are retained.",
  "externalModule.ondoc.settings.sourceGrants": "Selecting new mappings requires permission to read price lists, doctors and appointment types.",
  "externalModule.ondoc.settings.branchMapping": "Branch mapping",
  "externalModule.ondoc.settings.branchIdentifier": "OnDoc branch identifier",
  "externalModule.ondoc.settings.priceList": "Existing public price list",
  "externalModule.ondoc.settings.listCurrency": "Selected price list currency:",
  "externalModule.ondoc.settings.branchToken": "Branch Import API key",
  "externalModule.ondoc.settings.addBranch": "Add a branch mapping",
  "externalModule.ondoc.settings.doctorMapping": "Doctor mapping",
  "externalModule.ondoc.settings.doctor": "Terysling doctor",
  "externalModule.ondoc.settings.specialization": "OnDoc specialization code",
  "externalModule.ondoc.settings.adults": "Accepts adults",
  "externalModule.ondoc.settings.childrenMin": "Minimum child age, if children are accepted",
  "externalModule.ondoc.settings.childrenMax": "Maximum child age, if children are accepted",
  "externalModule.ondoc.settings.childrenHint": "Leave both age fields empty when children are not accepted. Otherwise enter both bounds from 0 to 18.",
  "externalModule.ondoc.settings.addDoctor": "Add a doctor mapping",
  "externalModule.ondoc.settings.serviceMapping": "Service mapping",
  "externalModule.ondoc.settings.service": "Service from the selected price list",
  "externalModule.ondoc.settings.visitKind": "Service delivery",
  "externalModule.ondoc.settings.visit": "In-person appointment",
  "externalModule.ondoc.settings.houseCall": "Doctor house call; catalogue only",
  "externalModule.ondoc.settings.appointmentType": "Published Terysling appointment type",
  "externalModule.ondoc.settings.catalogueOnly": "Catalogue only; no booking",
  "externalModule.ondoc.settings.duration": "Duration in minutes",
  "externalModule.ondoc.settings.addService": "Add a service mapping",
  "externalModule.ondoc.settings.scheduleHint": "Each provider schedule has one doctor, one workplace and services of one published appointment type version.",
  "externalModule.ondoc.settings.scheduleMapping": "Booking schedule mapping",
  "externalModule.ondoc.settings.workplace": "Doctor workplace",
  "externalModule.ondoc.settings.scheduledServices": "Services offered in this schedule",
  "externalModule.ondoc.settings.addSchedule": "Add a schedule mapping",
  "externalModule.ondoc.journal": "Exchange journal",
  "externalModule.ondoc.journalLimit": "The latest 200 operations are shown. Filters apply to this selection.",
  "externalModule.ondoc.journalFailed": "The exchange journal could not be loaded.",
  "externalModule.ondoc.journalRefresh": "Refresh the journal",
  "externalModule.ondoc.journalLoading": "Loading the exchange journal…",
  "externalModule.ondoc.journalHidden": "The exchange journal is unavailable while the module is hidden. Turn Hidden off to read it.",
  "externalModule.ondoc.journalUnavailable": "The exchange journal is not available. The module may have been hidden or removed for this organization.",
  "externalModule.ondoc.operationKind": "Operation",
  "externalModule.ondoc.operationCreated": "Created",
  "externalModule.ondoc.operationCode": "Connection code",
  "externalModule.ondoc.operationConnection": "Connection state update",
  "externalModule.ondoc.operationPublication": "Medical document publication",
  "externalModule.ondoc.operationWithdrawal": "Medical document withdrawal",
  "externalModule.ondoc.operationDoctor": "Doctor publication",
  "externalModule.ondoc.operationCatalogue": "Public services and prices",
  "externalModule.ondoc.operationAvailability": "Available appointment slots",
  "externalModule.ondoc.operationBooking": "Incoming appointment request",
  "externalModule.ondoc.operationCancellation": "Incoming cancellation request",
  "externalModule.ondoc.operationAppointment": "Appointment publication",
  "externalModule.ondoc.operationAcknowledgement": "Update acknowledgement",
  "externalModule.ondoc.operationOther": "Other operation",
  "externalModule.ondoc.operationPending": "Waiting",
  "externalModule.ondoc.operationProcessing": "Processing",
  "externalModule.ondoc.operationCompleted": "Completed",
  "externalModule.ondoc.operationFailed": "Exchange error",
  "externalModule.ondoc.operationAmbiguous": "Outcome unknown",
  "externalModule.ondoc.operationCanceled": "Removed from processing",
  "externalModule.ondoc.operationPaused": "Exchange paused",
  "externalModule.ondoc.openChart": "Open chart",
  "externalModule.ondoc.noOperations": "No exchange operations yet",
  "externalModule.ondoc.noOperationsDetail": "Operations will appear here when exchange begins.",

  "externalModule.ondoc.operationActions": "Actions",

  "externalModule.ondoc.medical.settingsTitle": "Medical documents",
  "externalModule.ondoc.medical.mappingHint": "Only selected protocols are published automatically after doctor confirmation for attached patients. Sending earlier documents requires manual selection.",
  "externalModule.ondoc.medical.sourceGrants": "Reading protocols and print forms requires the corresponding permissions.",
  "externalModule.ondoc.medical.mapping": "Document mapping",
  "externalModule.ondoc.medical.protocol": "Protocol",
  "externalModule.ondoc.medical.documentType": "OnDoc section",
  "externalModule.ondoc.medical.printForm": "Print form",
  "externalModule.ondoc.medical.language": "PDF language",
  "externalModule.ondoc.medical.consultations": "Consultations",
  "externalModule.ondoc.medical.exams": "Examinations",
  "externalModule.ondoc.medical.analyzes": "Analyses",
  "externalModule.ondoc.medical.addMapping": "Add protocol",
  "externalModule.ondoc.medical.mappingInvalid": "Select an active protocol and its compatible print form and language.",

  "externalModule.ondoc.medical.historyTitle": "Confirmed medical documents",
  "externalModule.ondoc.medical.navLabel": "Medical documents",
  "externalModule.ondoc.medical.navDetail": "Sending a patient’s confirmed documents to OnDoc",
  "externalModule.ondoc.medical.sentTitle": "Sent for this patient",
  "externalModule.ondoc.medical.refresh": "Refresh documents",
  "externalModule.ondoc.medical.historyFailed": "Could not load medical documents or exchange history.",
  "externalModule.ondoc.medical.manualHint": "Select a confirmed document to send its configured PDF to OnDoc. Documents confirmed while the patient was detached are sent only by explicit selection.",
  "externalModule.ondoc.medical.document": "Document",
  "externalModule.ondoc.medical.confirmedAt": "Confirmed on",
  "externalModule.ondoc.medical.author": "Author",
  "externalModule.ondoc.medical.send": "Send to OnDoc",
  "externalModule.ondoc.medical.sendNamed": "Send {name} to OnDoc",
  "externalModule.ondoc.medical.sending": "Accepting…",
  "externalModule.ondoc.medical.cancelSend": "Cancel",
  "externalModule.ondoc.medical.confirmSend": "Queue the PDF of this exact confirmed document using the organization’s protocol mapping? The patient must currently be attached. Acceptance does not mean publication has completed.",
  "externalModule.ondoc.medical.accepted": "Selection accepted. The exchange journal shows the result.",
  "externalModule.ondoc.medical.sendFailed": "Could not accept the selected document for publication. Your selection is retained.",
  "externalModule.ondoc.medical.mappingMissing": "This protocol has no usable OnDoc section, print form or language mapping. Ask the organization administrator to configure it.",
  "externalModule.ondoc.medical.historyIncomplete": "The confirmed document has no complete historical print data. It cannot be sent using current data as a substitute.",
  "externalModule.ondoc.medical.attachmentRequired": "The patient must currently be attached to OnDoc for this document’s branch or the organization network.",
  "externalModule.ondoc.medical.sourceChanged": "The selected document has changed or is no longer current and confirmed. Refresh the list and select the current revision.",
  "externalModule.ondoc.medical.doctorPending": "The document’s doctor has not yet been published to OnDoc. Try after the doctor mapping has been published.",
  "externalModule.ondoc.medical.noDocuments": "No confirmed documents",
  "externalModule.ondoc.medical.noDocumentsDetail": "This patient has no current doctor-confirmed documents available for selection.",
  "externalModule.ondoc.medical.journalLimit": "The latest 200 publication and withdrawal operations for this patient are shown.",

  "externalModule.ondoc.medical.operationPending": "This document already has an unresolved exchange operation. Check its result before selecting another publication.",

  "externalModule.ondoc.outcome.openNamed": "Review the outcome of {name}",
  "externalModule.ondoc.outcome.title": "Confirm OnDoc outcome",
  "externalModule.ondoc.outcome.hint": "Check this exact operation in OnDoc before recording its result. A confirmed absence permits another attempt; a confirmed completed operation will be recorded without repeating it.",
  "externalModule.ondoc.outcome.phase": "Operation to verify",
  "externalModule.ondoc.outcome.reference": "Exact OnDoc reference",
  "externalModule.ondoc.outcome.version": "Document version",
  "externalModule.ondoc.outcome.observation": "Verified result",
  "externalModule.ondoc.outcome.applied": "Performed in OnDoc",
  "externalModule.ondoc.outcome.notApplied": "Not performed in OnDoc",
  "externalModule.ondoc.outcome.checked": "I checked this operation in OnDoc and confirm the selected result.",
  "externalModule.ondoc.outcome.confirm": "Record verified result",
  "externalModule.ondoc.outcome.queued": "Decision recorded. The worker will continue when exchange is resumed; the journal will show the result.",
  "externalModule.ondoc.outcome.fileId": "Uploaded OnDoc file ID",
  "externalModule.ondoc.outcome.fileHint": "Enter the verified numeric ID of the exact uploaded PDF, not the document reference.",
  "externalModule.ondoc.outcome.fileInvalid": "Enter a valid positive OnDoc file ID for the completed upload.",
  "externalModule.ondoc.outcome.failed": "Could not review or record this outcome. Your entries are retained.",
  "externalModule.ondoc.outcome.notReviewable": "This operation is being processed, already resolved, or requires automatic local reconciliation. Refresh the journal to see its current state.",
  "externalModule.ondoc.outcome.changed": "The operation changed after you inspected it. Your decision has not been recorded; reload and check the current operation before confirming.",
  "externalModule.ondoc.outcome.reload": "Reload operation and clear entered decision",
  "externalModule.ondoc.outcome.reloadFailed": "Could not reload the operation. The decision you entered is retained.",
  "externalModule.ondoc.outcome.unsaved": "Unrecorded OnDoc outcome decision",
  "externalModule.ondoc.outcome.discard": "Discard the outcome decision and file ID you entered? No result will be recorded.",
  "externalModule.ondoc.outcome.upload": "Upload the PDF",
  "externalModule.ondoc.outcome.create": "Publish the medical document",
  "externalModule.ondoc.outcome.update": "Update the published medical document",
  "externalModule.ondoc.outcome.delete": "Withdraw the medical document",
  "externalModule.ondoc.outcome.eventCreate": "Create the appointment",
  "externalModule.ondoc.outcome.eventUpdate": "Update the appointment",
  "externalModule.ondoc.outcome.eventCancel": "Cancel the appointment",
  "externalModule.ondoc.outcome.doctorCreate": "Publish the doctor",
  "externalModule.ondoc.outcome.appointmentAccept": "Accept the appointment request",
  "externalModule.ondoc.outcome.appointmentReject": "Reject the appointment request",
  "externalModule.ondoc.outcome.cancellationAccept": "Accept the cancellation request",

} as const
